import { forwardRef, useMemo } from "react";
import styles from "./PoPdfView.module.css";
import { format, parseISO } from "date-fns";
import parse from "html-react-parser";

const safeFormatDateParts = (value) => {
  if (!value) return { date: "—", day: "" };

  try {
    const parsed = parseISO(String(value));
    if (!Number.isNaN(parsed?.getTime?.())) {
      return {
        date: format(parsed, "dd MMM yyyy"),
        day: format(parsed, "EEEE"),
      };
    }
  } catch {
    // ignore and fall back
  }

  try {
    const parsed = new Date(value);
    if (!Number.isNaN(parsed?.getTime?.())) {
      return {
        date: format(parsed, "dd MMM yyyy"),
        day: format(parsed, "EEEE"),
      };
    }
  } catch {
    // ignore
  }

  return { date: String(value), day: "" };
};

const addressFormat = (address) => {
  if (!address) return "—";
  if (typeof address === "string") return address.trim() || "—";
  if (typeof address !== "object") return String(address || "—");

  const parts = Object.values(address).filter(Boolean).map(String);
  return parts.length ? parts.join(", ") : "—";
};

const formatCompactAmount = (amount, currency = "INR") => {
  const numericAmount = Number(amount || 0);
  const safeAmount = Number.isFinite(numericAmount) ? numericAmount : 0;
  const resolvedCurrency = String(currency || "INR").toUpperCase();

  if (resolvedCurrency === "INR") {
    return `₹ ${safeAmount.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }

  return `${safeAmount.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} ${resolvedCurrency}`;
};

const formatRoundedAmount = (amount, currency = "INR") => {
  const numericAmount = Number(amount || 0);
  const safeAmount = Number.isFinite(numericAmount) ? numericAmount : 0;

  const roundedAmount = Math.round(safeAmount).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const resolvedCurrency = String(currency || "INR").toUpperCase();

  if (resolvedCurrency === "INR") {
    return `₹ ${roundedAmount}`;
  }

  return `${roundedAmount} ${resolvedCurrency}`;
};

const PoPdfView = forwardRef(({ pdfData = {} }, ref) => {
  const {
    issuer_details = {},
    vendor_details = {},
    billing_details = {},
    shipping_details = {},
    pricing = {},
    terms = {},
    item_list = [],
    company_vendor_code = "",
    delivery_date = "",
    payment_date = "",
  } = pdfData || {};

  const {
    issuer_details_name,
    issuer_details_email,
    issuer_details_PAN,
    issuer_details_GSTIN,
    issuer_details_CIN,
    issuer_details_purchase_order_no,
    issuer_details_purchase_order_date,
    issuer_details_address,
  } = issuer_details || {};

  const {
    vendor_details_address,
    vendor_details_name,
    vendor_name,
    vendor_image_base64,
  } = vendor_details || {};

  const { billing_details_name, billing_details_address } = billing_details || {};
  const { shipping_details_name, shipping_details_address } = shipping_details || {};

  const normalizedTerms = Array.isArray(terms) ? terms[0] || {} : terms || {};

  const {
    payment_terms,
    validity,
    delivery_terms,
    mode_of_transportation,
    terms_and_conditions_of_purchase = "",
  } = normalizedTerms;

  const orderDateParts = safeFormatDateParts(issuer_details_purchase_order_date);
  const paymentDueParts = safeFormatDateParts(payment_date);
  const deliveryByParts = safeFormatDateParts(delivery_date);

  const resolvedCurrency =
    pricing?.currency_type ||
    pricing?.currency ||
    item_list?.[0]?.currency_type ||
    "INR";

  const lineItems = useMemo(() => {
    return (Array.isArray(item_list) ? item_list : []).map((item) => {
      const unitPrice = Number(item?.unit_price ?? item?.UnitPrice ?? 0);
      const quantity = Number(item?.quantity ?? item?.qty ?? 0);
      const taxPct = Number(item?.tax ?? item?.tax_percent ?? 0);

      const totalPrice = Number(
        item?.total_price ?? item?.amount ?? unitPrice * quantity
      );

      const partNo =
        item?.part_number ||
        item?.part_no ||
        item?.part ||
        item?.item_code ||
        item?.itemCode ||
        "";

      const itemName = item?.item_name || item?.itemName || item?.name || "";

      return {
        ...item,
        description: item?.description || item?.description_of_goods || "",
        hsn: item?.hsn_code || item?.hsn || "—",
        qty: Number.isFinite(quantity) ? quantity : 0,
        unit: item?.unit || item?.uom || "",
        taxPct: Number.isFinite(taxPct) ? taxPct : 0,
        unitPrice: Number.isFinite(unitPrice) ? unitPrice : 0,
        totalPrice: Number.isFinite(totalPrice) ? totalPrice : 0,
        partNo,
        itemName,
      };
    });
  }, [item_list]);

  const grandTotal = useMemo(() => {
    const grandTotalFromPricing = Number(
      pricing?.grand_total ?? pricing?.grandTotal ?? NaN
    );

    if (Number.isFinite(grandTotalFromPricing) && grandTotalFromPricing > 0) {
      return Math.round(grandTotalFromPricing);
    }

    const subtotalFromPricing = Number(pricing?.subtotal ?? pricing?.total ?? NaN);

    const subtotalFromLines = lineItems.reduce(
      (sum, item) => sum + Number(item?.totalPrice || 0),
      0
    );

    const resolvedSubtotal =
      Number.isFinite(subtotalFromPricing) && subtotalFromPricing > 0
        ? subtotalFromPricing
        : subtotalFromLines;

    const gstFromLines = lineItems.reduce((sum, item) => {
      const base = Number(item?.totalPrice || 0);
      const pct = Number(item?.taxPct || 0);

      if (!Number.isFinite(base) || !Number.isFinite(pct) || pct <= 0) {
        return sum;
      }

      return sum + (base * pct) / 100;
    }, 0);

    return Math.round(resolvedSubtotal + gstFromLines);
  }, [lineItems, pricing]);

  const vendorDisplayName =
    vendor_details_name || vendor_name || issuer_details?.vendor_name || "—";

  return (
    <div className={`${styles.poPdfContainer} po-pdf-container`} ref={ref}>
      <header className={`${styles.topHeader} po-top-header`}>
        <div className={styles.brandBlock}>
          {vendor_image_base64 ? (
            <img
              alt="logo"
              src={vendor_image_base64}
              className={styles.brandLogo}
            />
          ) : (
            <div className={styles.brandMark}>T</div>
          )}

          <div className={styles.brandText}>
            <div className={styles.brandName}>{issuer_details_name || "—"}</div>
            <div className={styles.brandTagline}>#WEALSOMAKETOMORROW</div>
          </div>
        </div>

        <div className={styles.poNumberBlock}>
          <div className={styles.poLabel}>PURCHASE ORDER No</div>
          <div className={styles.poNumber}>
            {issuer_details_purchase_order_no || "—"}
          </div>
        </div>
      </header>

      <div className={styles.dividerLine} />

      <section className={`${styles.heroRow} po-hero-row`}>
        <div className={styles.heroLeft}>
          <div className={styles.sectionEyebrow}>PROCUREMENT DOCUMENT</div>

          <div className={styles.heroTitle}>
            A formal request
            <br />
            for <span className={styles.heroAccent}>goods &amp; services.</span>
          </div>
        </div>

        <div className={styles.heroRight}>
          <div className={styles.sectionEyebrow}>
            Issued by {issuer_details_name || "—"}
          </div>

          <div className={styles.issuerAddress}>
            {addressFormat(issuer_details_address)}
          </div>

          {issuer_details_email ? (
            <div className={styles.issuerEmail}>{issuer_details_email}</div>
          ) : null}
        </div>
      </section>

      <section className={`${styles.kpiRow} ${styles.kpiRowThree} po-kpi-row`}>
        <div className={styles.kpiItem}>
          <div className={styles.kpiLabel}>PAN</div>
          <div className={`${styles.kpiValue} ${styles.kpiValueMono}`}>
            {issuer_details_PAN || "—"}
          </div>
        </div>

        <div className={styles.kpiItem}>
          <div className={styles.kpiLabel}>GSTIN</div>
          <div className={`${styles.kpiValue} ${styles.kpiValueMono}`}>
            {issuer_details_GSTIN || "—"}
          </div>
        </div>

        <div className={styles.kpiItem}>
          <div className={styles.kpiLabel}>CIN</div>
          <div className={`${styles.kpiValue} ${styles.kpiValueMono}`}>
            {issuer_details_CIN || "—"}
          </div>
        </div>
      </section>

      <section className={`${styles.kpiRow} po-kpi-row`}>
        <div className={styles.kpiItem}>
          <div className={styles.kpiLabel}>ORDER DATE</div>
          <div className={styles.kpiValue}>{orderDateParts.date}</div>

          {orderDateParts.day ? (
            <div className={styles.kpiSubValue}>{orderDateParts.day}</div>
          ) : null}
        </div>

        <div className={styles.kpiItem}>
          <div className={styles.kpiLabel}>PAYMENT DUE</div>
          <div className={styles.kpiValue}>{paymentDueParts.date}</div>

          {payment_terms ? (
            <div className={styles.kpiSubValue}>{payment_terms}</div>
          ) : null}
        </div>

        <div className={styles.kpiItem}>
          <div className={styles.kpiLabel}>DELIVERY BY</div>
          <div className={styles.kpiValue}>{deliveryByParts.date}</div>

          {delivery_terms ? (
            <div className={styles.kpiSubValue}>{delivery_terms}</div>
          ) : null}
        </div>

        <div className={styles.kpiItem}>
          <div className={styles.kpiLabel}>VALIDITY</div>
          <div className={styles.kpiValue}>{validity || "—"}</div>
          <div className={styles.kpiSubValue}>From issue</div>
        </div>
      </section>

      <section className={`${styles.partyRow} po-party-row`}>
        <div className={styles.partyCard}>
          <div className={styles.partyHeader}>
            <span className={styles.partyDot} />
            <span className={styles.partyLabel}>VENDOR</span>
          </div>

          <div className={styles.partyName}>{vendorDisplayName}</div>

          <div className={styles.partyAddress}>
            {addressFormat(vendor_details_address)}
          </div>

          {company_vendor_code ? (
            <div className={styles.partyMeta}>{company_vendor_code}</div>
          ) : null}
        </div>

        <div className={styles.partyCard}>
          <div className={styles.partyHeader}>
            <span className={styles.partyDot} />
            <span className={styles.partyLabel}>BILL TO</span>
          </div>

          <div className={styles.partyName}>
            {billing_details_name || issuer_details_name || "—"}
          </div>

          <div className={styles.partyAddress}>
            {addressFormat(billing_details_address)}
          </div>
        </div>

        <div className={styles.partyCard}>
          <div className={styles.partyHeader}>
            <span className={styles.partyDot} />
            <span className={styles.partyLabel}>SHIP TO</span>
          </div>

          <div className={styles.partyName}>
            {shipping_details_name || issuer_details_name || "—"}
          </div>

          <div className={styles.partyAddress}>
            {addressFormat(shipping_details_address)}
          </div>
        </div>
      </section>

      <section className={`${styles.lineItemsSection} po-line-items-section`}>
        <div className={`${styles.lineItemsHeader} po-line-items-header`}>
          <div className={styles.lineItemsTitle}>Line Items</div>

          <div className={styles.lineItemsMeta}>
            {String(lineItems.length || 0).padStart(2, "0")} ITEMS •{" "}
            {String(resolvedCurrency || "INR").toUpperCase()}
          </div>
        </div>

        <div className={styles.lineItemsTable}>
          <div
            className={`${styles.tableRow} ${styles.tableHead} po-table-head`}
          >
            <div className={styles.colNo}>Sl No</div>
            <div className={styles.colPartNo}>PART NO</div>
            <div className={styles.colItemName}>ITEM NAME</div>
            <div className={styles.colDesc}>DESCRIPTION</div>
            <div className={styles.colHsn}>HSN</div>
            <div className={styles.colQty}>QTY</div>
            <div className={styles.colTax}>TAX %</div>
            <div className={styles.colRate}>RATE</div>
            <div className={styles.colAmount}>AMOUNT</div>
          </div>

          {lineItems.length > 0 ? (
            lineItems.map((item, index) => (
              <div
                key={`${item?.item_code || item?.part_number || index}`}
                className={`${styles.tableRow} po-table-row`}
              >
                <div className={styles.colNo}>
                  <span className={styles.itemIndex}>
                    {String(index + 1).padStart(2, "0")}
                  </span>
                </div>

                <div className={styles.colPartNo}>
                  <div className={styles.itemPartNo}>{item.partNo || "—"}</div>
                </div>

                <div className={styles.colItemName}>
                  <div className={styles.itemName}>{item.itemName || "—"}</div>
                </div>

                <div className={styles.colDesc}>
                  <div className={styles.itemTitle}>
                    {item.description || "—"}
                  </div>
                </div>

                <div className={styles.colHsn}>{item.hsn || "—"}</div>

                <div className={styles.colQty}>
                  <div className={styles.qtyValue}>{item.qty}</div>

                  {item.unit ? (
                    <div className={styles.qtyUnit}>{item.unit}</div>
                  ) : null}
                </div>

                <div className={styles.colTax}>{Number(item.taxPct || 0)}%</div>

                <div className={styles.colRate}>
                  {formatCompactAmount(item.unitPrice, resolvedCurrency)}
                </div>

                <div className={styles.colAmount}>
                  {formatCompactAmount(item.totalPrice, resolvedCurrency)}
                </div>
              </div>
            ))
          ) : (
            <div className={`${styles.tableRow} po-table-row`}>
              <div className={styles.colNo}>—</div>
              <div className={styles.colPartNo}>—</div>
              <div className={styles.colItemName}>No items</div>
              <div className={styles.colDesc}>—</div>
              <div className={styles.colHsn}>—</div>
              <div className={styles.colQty}>—</div>
              <div className={styles.colTax}>—</div>
              <div className={styles.colRate}>—</div>
              <div className={styles.colAmount}>—</div>
            </div>
          )}
        </div>
      </section>

      <section className={`${styles.totalRow} po-total-row`}>
        <div className={styles.totalLeft}>
          <div className={styles.totalNoteLabel}>
            ALL AMOUNTS IN {String(resolvedCurrency || "INR").toUpperCase()}
          </div>

          <div className={styles.totalNoteText}>
            This is a computer-generated document. No signature is required.
          </div>
        </div>

        <div className={styles.totalRight}>
          <div className={styles.totalLabel}>GRAND TOTAL</div>
          <div className={styles.totalValue}>
            {formatRoundedAmount(grandTotal, resolvedCurrency)}
          </div>
        </div>
      </section>

      <section className={`${styles.bottomBar} po-bottom-bar`}>
        <div className={styles.bottomItem}>
          <div className={styles.bottomLabel}>PAYMENT</div>
          <div className={styles.bottomValue}>{payment_terms || "—"}</div>
        </div>

        <div className={styles.bottomItem}>
          <div className={styles.bottomLabel}>DELIVERY</div>
          <div className={styles.bottomValue}>{delivery_terms || "—"}</div>
        </div>

        <div className={styles.bottomItem}>
          <div className={styles.bottomLabel}>TRANSPORT</div>
          <div className={styles.bottomValue}>
            {mode_of_transportation || "—"}
          </div>
        </div>

        <div className={styles.bottomItem}>
          <div className={styles.bottomLabel}>VALIDITY</div>
          <div className={styles.bottomValue}>{validity || "—"}</div>
        </div>
      </section>

      {terms_and_conditions_of_purchase ? (
        <div className={`${styles.termsPageBreakMarker} po-terms-page`} />
      ) : null}

      {terms_and_conditions_of_purchase ? (
        <section
          className={`${styles.termsSection} ${styles.termsSectionPageBreak} po-terms-section`}
        >
          <div className={styles.termsTitle}>Terms &amp; Conditions</div>

          <div className={styles.termsParsed}>
            {parse(terms_and_conditions_of_purchase)}
          </div>
        </section>
      ) : null}

      <footer className={`${styles.footerRow} po-footer-row`}>
        <div className={styles.footerLeft}>
          PO {issuer_details_purchase_order_no || "—"} • ISSUED{" "}
          {orderDateParts.date}
        </div>

        <div className={styles.footerRight}>01 / 01</div>
      </footer>
    </div>
  );
});

PoPdfView.displayName = "PoPdfView";

export default PoPdfView;
